import type { CSSProperties } from 'react';
import type React from 'react';
import { Component } from 'react';
import interact from 'interactjs';
import type { Moment } from 'moment';
import moment from 'moment';

import { _get } from '../utility/generic';
import { composeEvents } from '../utility/events';
import { defaultItemRenderer } from './defaultItemRenderer';
import type { ItemDimensions } from '../utility/calendar';
import { getSumOffset, getSumScroll } from '../utility/dom-helpers';
import {
  leftResizeStyle,
  overridableStyles,
  rightResizeStyle,
  selectedAndCanMove,
  selectedAndCanResizeLeft,
  selectedAndCanResizeLeftAndDragLeft,
  selectedAndCanResizeRight,
  selectedAndCanResizeRightAndDragRight,
  selectedStyle,
} from './styles';
import type { TimelineContextValue, TimelineItemBase } from '../types';

export type GetItemPropsParams = {
  className?: string;
  style?: CSSProperties;
  onMouseUp?: (e: React.MouseEvent) => void;
  onMouseDown?: (e: React.MouseEvent) => void;
  onContextMenu?: (e: React.MouseEvent) => void;
  onDoubleClick?: (e: React.MouseEvent) => void;
  onTouchEnd?: (e: React.TouchEvent<HTMLElement>) => void;
  onTouchStart?: (e: React.TouchEvent<HTMLElement>) => void;
};

export type RealItemProps = {
  title: string;
  className: string;
  key: string | number;
  style: CSSProperties;
  ref: null | ((el: HTMLElement | null) => HTMLElement);
  onMouseUp: (event: React.MouseEvent) => void;
  onMouseDown: (event: React.MouseEvent) => void;
  onContextMenu: (event: React.MouseEvent) => void;
  onDoubleClick: (event: React.MouseEvent) => void;
  onTouchEnd: (event: React.TouchEvent<HTMLElement>) => void;
  onTouchStart: (event: React.TouchEvent<HTMLElement>) => void;
};

export type ItemRendererGetResizePropsReturnType = {
  left?:
    | {
        ref: React.Ref<HTMLDivElement>;
        style: React.CSSProperties;
        className: string;
      }
    | undefined;
  right?:
    | {
        ref: React.Ref<HTMLDivElement>;
        style: React.CSSProperties;
        className: string;
      }
    | undefined;
};

export type ItemRendererProps<Item extends TimelineItemBase> = {
  timelineContext: TimelineContextValue;
  getItemProps: (props?: GetItemPropsParams) => RealItemProps;
  getResizeProps: () => ItemRendererGetResizePropsReturnType;
  itemContext: ItemContext;
  item: Item;
};

export type ItemProps<Item extends TimelineItemBase> = {
  item: Item;
  keys: {
    itemIdKey: keyof Item;
    itemTitleKey: keyof Item;
    itemDivTitleKey: keyof Item;
    itemTimeStartKey: keyof Item;
    itemTimeEndKey: keyof Item;
  };
  itemRenderer: (props: ItemRendererProps<Item>) => React.ReactNode;

  getTimelineContext: () => TimelineContextValue;

  canvasTimeStart: number;
  canvasTimeEnd: number;
  canvasWidth: number;
  canvasTop: number;
  timeRatio: number;
  pixelsPerMs: number;
  order: {
    index: number;
  };

  dragSnap?: number;
  selected?: boolean;
  minResizeWidth?: number;

  canMove?: boolean;
  canResizeLeft: boolean;
  canResizeRight: boolean;
  canChangeGroup?: boolean;

  onSelect?: (
    itemId: string | number,
    clickType: 'click' | 'touch',
    e: React.MouseEvent | React.TouchEvent<HTMLElement>,
  ) => void;
  onDrag?: (
    itemId: string | number,
    dragTime: number | Moment,
    indexDelta: number,
  ) => void;
  onDrop?: (
    itemId: string | number,
    dragTime: number | Moment,
    indexDelta: number,
  ) => void;
  onResizing?: (
    itemId: string | number,
    resizeTime: number,
    resizeEdge: null | 'left' | 'right',
  ) => void;
  onResized?: (
    itemId: string | number,
    resizeTime: number,
    resizeEdge: null | 'left' | 'right',
    resizeTimeDelta: number,
  ) => void;
  onContextMenu?: (itemId: string | number, e: React.MouseEvent) => void;

  canSelect?: boolean;
  dimensions: ItemDimensions['dimensions'];
  groupTops: number[];
  useResizeHandle?: boolean;
  moveResizeValidator?: (
    event: 'move' | 'resize',
    item: Item,
    dragTime: number | Moment,
    resizeEdge?: null | 'left' | 'right',
  ) => number;
  onItemDoubleClick?: (itemId: string | number, e: React.MouseEvent) => void;
  onItemTap?: (
    itemId: string | number,
    e: React.TouchEvent<HTMLElement>,
  ) => void;

  scrollRef?: HTMLElement | null;
};

export type ItemState = {
  dragging: boolean;
  dragGroupDelta: null | number;
  preDragPosition: null | {
    x: number;
    y: number;
  };
  dragTime: null | number | Moment;
  dragStart: null | {
    x: number;
    y: number;
    offset: number;
  };
  resizing: boolean;
  resizeEdge: null | 'left' | 'right';
  resizeStart: null | number;
  interactMounted: boolean;
  resizeTime: null | number;
  touchStart: { x: number; y: number };
};

export type ItemContext = {
  canMove: boolean;
  canResizeRight: boolean;
  dragGroupDelta: number | null;
  dragStart: {
    x: number;
    y: number;
    offset: number;
  } | null;
  resizing: boolean;
  resizeStart: number | null;
  title: string;
  resizeTime: number | null;
  dragging: boolean;
  useResizeHandle: boolean | undefined;
  dragTime: number | Moment | null;
  canResizeLeft: boolean;
  resizeEdge: 'left' | 'right' | null;
  width: number;
  selected: boolean | undefined;
  dimensions: {
    stack: boolean;
    top: number | null;
    left: number;
    collisionLeft: number;
    width: number;
    collisionWidth: number;
    order?: {
      index: number;
    };
    height: number;
  };
  timeRatio: number;
  pixelsPerMs: number;
};

export default class Item<
  Item extends TimelineItemBase,
  Props extends ItemProps<Item>,
> extends Component<Props, ItemState> {
  static defaultProps = {
    selected: false,
    itemRenderer: defaultItemRenderer,
  };

  itemId: string | number;
  itemTitle: string;
  itemDivTitle: string;
  itemTimeStart: number;
  itemTimeEnd: number;
  startedClicking: boolean;
  startedTouching: boolean;
  item: HTMLElement | null;
  dragLeft: HTMLElement | null;
  dragRight: HTMLElement | null;

  constructor(props: Props) {
    super(props);

    this.cacheDataFromProps(props);

    this.state = {
      interactMounted: false,

      dragging: false,
      dragStart: null,
      preDragPosition: null,
      dragTime: null,
      dragGroupDelta: null,

      resizing: false,
      resizeEdge: null,
      resizeStart: null,
      resizeTime: null,
      touchStart: { x: 0, y: 0 },
    };
  }

  shouldComponentUpdate(nextProps: Props, nextState: ItemState) {
    return (
      nextState.dragging !== this.state.dragging ||
      nextState.dragTime !== this.state.dragTime ||
      nextState.dragGroupDelta !== this.state.dragGroupDelta ||
      nextState.resizing !== this.state.resizing ||
      nextState.resizeTime !== this.state.resizeTime ||
      nextProps.keys !== this.props.keys ||
      nextProps.selected !== this.props.selected ||
      nextProps.item !== this.props.item ||
      nextProps.canvasTimeStart !== this.props.canvasTimeStart ||
      nextProps.canvasTimeEnd !== this.props.canvasTimeEnd ||
      nextProps.canvasWidth !== this.props.canvasWidth ||
      (nextProps.order ? nextProps.order.index : undefined) !==
        (this.props.order ? this.props.order.index : undefined) ||
      nextProps.dragSnap !== this.props.dragSnap ||
      nextProps.minResizeWidth !== this.props.minResizeWidth ||
      nextProps.canChangeGroup !== this.props.canChangeGroup ||
      nextProps.canSelect !== this.props.canSelect ||
      nextProps.canMove !== this.props.canMove ||
      nextProps.canResizeLeft !== this.props.canResizeLeft ||
      nextProps.canResizeRight !== this.props.canResizeRight ||
      nextProps.dimensions !== this.props.dimensions
    );
  }

  cacheDataFromProps(props: Props) {
    const { item, keys } = props;
    this.itemId = _get(item, keys.itemIdKey);
    this.itemTitle = _get(item, keys.itemTitleKey);
    this.itemDivTitle = keys.itemDivTitleKey
      ? _get(item, keys.itemDivTitleKey)
      : this.itemTitle;
    this.itemTimeStart = _get(item, keys.itemTimeStartKey);
    this.itemTimeEnd = _get(item, keys.itemTimeEndKey);
  }

  dragTimeSnap(dragTime: number, considerOffset: boolean) {
    const { dragSnap } = this.props;
    if (dragSnap) {
      const offset = considerOffset ? moment().utcOffset() * 60 * 1000 : 0;
      return Math.round(dragTime / dragSnap) * dragSnap - (offset % dragSnap);
    } else {
      return dragTime;
    }
  }

  resizeTimeSnap(dragTime: number) {
    const { dragSnap } = this.props;
    if (dragSnap) {
      const endTime = this.itemTimeEnd % dragSnap;
      return Math.round((dragTime - endTime) / dragSnap) * dragSnap + endTime;
    } else {
      return dragTime;
    }
  }

  dragTime(e: React.MouseEvent) {
    if (this.state.dragging) {
      return this.dragTimeSnap(
        this.timeFor(e) + (this.state.dragStart?.offset ?? 0),
        true,
      );
    } else {
      return moment(this.itemTimeStart);
    }
  }

  timeFor(e: React.MouseEvent) {
    const { timeRatio, scrollRef, canvasTimeStart } = this.props;
    const offset = getSumOffset(scrollRef).offsetLeft;
    const scrolls = getSumScroll(scrollRef);

    return (
      (e.pageX - offset + scrolls.scrollLeft) * timeRatio + canvasTimeStart
    );
  }

  dragGroupDelta(e: React.MouseEvent) {
    const { groupTops, order } = this.props;
    if (this.state.dragging) {
      if (!this.props.canChangeGroup) {
        return 0;
      }
      let groupDelta = 0;

      const offset = getSumOffset(this.props.scrollRef).offsetTop;
      const scroll = getSumScroll(this.props.scrollRef).scrollTop;
      const y = e.pageY - offset + scroll - this.props.canvasTop;

      for (const [index, groupTop] of groupTops.entries()) {
        if (y > groupTop) {
          groupDelta = index - order.index;
        } else {
          break;
        }
      }

      if (this.props.order.index + groupDelta < 0) {
        return 0 - this.props.order.index;
      } else {
        return groupDelta;
      }
    } else {
      return 0;
    }
  }

  resizeTimeDelta(e: React.MouseEvent, resizeEdge: null | 'left' | 'right') {
    const length = this.itemTimeEnd - this.itemTimeStart;
    const { timeRatio, dragSnap } = this.props;
    const timeDelta = this.dragTimeSnap(
      (e.pageX - (this.state.resizeStart ?? 0)) * timeRatio,
      false,
    );

    const snap = dragSnap || 1000;
    if (length + (resizeEdge === 'left' ? -timeDelta : timeDelta) < snap) {
      if (resizeEdge === 'left') {
        return length - snap;
      } else {
        return snap - length;
      }
    } else {
      return timeDelta;
    }
  }

  mountInteract() {
    const target = this.item;
    if (!target) {
      return;
    }
    const leftResize = this.props.useResizeHandle
      ? '.rct-item-handler-resize-left'
      : true;
    const rightResize = this.props.useResizeHandle
      ? '.rct-item-handler-resize-right'
      : true;

    const selected = this.props.selected;
    interact(target)
      .resizable({
        edges: {
          left: this.canResizeLeft() && leftResize,
          right: this.canResizeRight() && rightResize,
          top: false,
          bottom: false,
        },
        enabled: selected && (this.canResizeLeft() || this.canResizeRight()),
      })
      .draggable({
        enabled: selected && this.canMove(),
      })
      .styleCursor(false)
      .on('dragstart', (e) => {
        if (selected) {
          const clickTime = this.timeFor(e);
          this.setState({
            dragging: true,
            dragStart: {
              x: e.pageX,
              y: e.pageY,
              offset: this.itemTimeStart - clickTime,
            },
            preDragPosition: { x: e.target.offsetLeft, y: e.target.offsetTop },
            dragTime: this.itemTimeStart,
            dragGroupDelta: 0,
          });
        } else {
          return false;
        }
      })
      .on('dragmove', (e) => {
        if (this.state.dragging) {
          let dragTime = this.dragTime(e);
          const dragGroupDelta = this.dragGroupDelta(e);
          if (this.props.moveResizeValidator) {
            dragTime = this.props.moveResizeValidator(
              'move',
              this.props.item,
              dragTime,
            );
          }

          if (this.props.onDrag) {
            this.props.onDrag(
              this.itemId,
              dragTime,
              this.props.order.index + dragGroupDelta,
            );
          }

          this.setState({
            dragTime: dragTime,
            dragGroupDelta: dragGroupDelta,
          });
        }
      })
      .on('dragend', (e) => {
        if (this.state.dragging) {
          if (this.props.onDrop) {
            let dragTime = this.dragTime(e);

            if (this.props.moveResizeValidator) {
              dragTime = this.props.moveResizeValidator(
                'move',
                this.props.item,
                dragTime,
              );
            }

            this.props.onDrop(
              this.itemId,
              dragTime,
              this.props.order.index + this.dragGroupDelta(e),
            );
          }

          this.setState({
            dragging: false,
            dragStart: null,
            preDragPosition: null,
            dragTime: null,
            dragGroupDelta: null,
          });
        }
      })
      .on('resizestart', (e) => {
        if (selected) {
          this.setState({
            resizing: true,
            resizeEdge: null, // we don't know yet
            resizeStart: e.pageX,
            resizeTime: 0,
          });
        } else {
          return false;
        }
      })
      .on('resizemove', (e) => {
        if (this.state.resizing) {
          let resizeEdge = this.state.resizeEdge;

          if (!resizeEdge) {
            resizeEdge = e.deltaRect.left !== 0 ? 'left' : 'right';
            this.setState({ resizeEdge });
          }
          let resizeTime = this.resizeTimeSnap(this.timeFor(e));

          if (this.props.moveResizeValidator) {
            resizeTime = this.props.moveResizeValidator(
              'resize',
              this.props.item,
              resizeTime,
              resizeEdge,
            );
          }

          if (this.props.onResizing) {
            this.props.onResizing(this.itemId, resizeTime, resizeEdge);
          }

          this.setState({
            resizeTime,
          });
        }
      })
      .on('resizeend', (e) => {
        if (this.state.resizing) {
          const { resizeEdge } = this.state;
          let resizeTime = this.resizeTimeSnap(this.timeFor(e));

          if (this.props.moveResizeValidator) {
            resizeTime = this.props.moveResizeValidator(
              'resize',
              this.props.item,
              resizeTime,
              resizeEdge,
            );
          }

          if (this.props.onResized) {
            this.props.onResized(
              this.itemId,
              resizeTime,
              resizeEdge,
              this.resizeTimeDelta(e, resizeEdge),
            );
          }
          this.setState({
            resizing: false,
            resizeStart: null,
            resizeEdge: null,
            resizeTime: null,
          });
        }
      })
      .on('tap', (e) => {
        this.actualClick(e, e.pointerType === 'mouse' ? 'click' : 'touch');
      });

    this.setState({
      interactMounted: true,
    });
  }

  canResizeLeft(props = this.props) {
    const minResizeWidth = props.minResizeWidth;
    if (!props.canResizeLeft || minResizeWidth == null) {
      return false;
    }
    const width = props.dimensions.width;
    return width >= minResizeWidth;
  }

  canResizeRight(props = this.props) {
    const minResizeWidth = props.minResizeWidth;
    if (!props.canResizeRight || minResizeWidth == null) {
      return false;
    }
    const width = props.dimensions.width;
    return width >= minResizeWidth;
  }

  canMove(props = this.props) {
    return Boolean(props.canMove);
  }

  componentDidUpdate(prevProps: Props) {
    this.cacheDataFromProps(this.props);
    let { interactMounted } = this.state;
    const prevSelected = prevProps.selected;
    const couldDrag = prevSelected && this.canMove(prevProps);
    const couldResizeLeft = prevSelected && this.canResizeLeft(prevProps);
    const couldResizeRight = prevSelected && this.canResizeRight(prevProps);
    const { useResizeHandle, selected } = this.props;
    const willBeAbleToDrag = selected && this.canMove(this.props);
    const willBeAbleToResizeLeft = selected && this.canResizeLeft(this.props);
    const willBeAbleToResizeRight = selected && this.canResizeRight(this.props);

    if (this.item) {
      if (selected && !interactMounted) {
        this.mountInteract();
        interactMounted = true;
      }
      if (
        interactMounted &&
        (couldResizeLeft !== willBeAbleToResizeLeft ||
          couldResizeRight !== willBeAbleToResizeRight)
      ) {
        const leftResize = useResizeHandle ? Boolean(this.dragLeft) : true;
        const rightResize = useResizeHandle ? Boolean(this.dragRight) : true;

        interact(this.item).resizable({
          enabled: willBeAbleToResizeLeft || willBeAbleToResizeRight,
          edges: {
            top: false,
            bottom: false,
            left: willBeAbleToResizeLeft && leftResize,
            right: willBeAbleToResizeRight && rightResize,
          },
        });
      }
      if (interactMounted && couldDrag !== willBeAbleToDrag) {
        interact(this.item).draggable({ enabled: willBeAbleToDrag });
      }
    } else {
      interactMounted = false;
    }
    this.setState({
      interactMounted,
    });
  }

  onMouseDown = (e: React.MouseEvent) => {
    if (!this.state.interactMounted) {
      e.preventDefault();
      this.startedClicking = true;
    }
  };

  onMouseUp = (e: React.MouseEvent) => {
    if (!this.state.interactMounted && this.startedClicking) {
      this.startedClicking = false;
      this.actualClick(e, 'click');
    }
  };

  onTouchStart = (e: React.TouchEvent<HTMLElement>) => {
    const touch = e.touches[0];
    this.setState({
      touchStart: {
        x: touch.clientX,
        y: touch.clientY,
      },
    });
    if (!this.state.interactMounted) {
      this.startedTouching = true;
    }
  };

  onTouchEnd = (e: React.TouchEvent<HTMLElement>) => {
    const touch = e.changedTouches[0];
    const touchEnd = { x: touch.clientX, y: touch.clientY };
    const { touchStart } = this.state;
    const distanceX = Math.abs(touchEnd.x - touchStart.x);
    const distanceY = Math.abs(touchEnd.y - touchStart.y);

    // max distance for a touch to be considered
    // a tap, rather than a scroll gesture
    const tapMovementThreshold = 10;

    if (!this.state.interactMounted && this.startedTouching) {
      this.startedTouching = false;

      if (
        distanceX < tapMovementThreshold &&
        distanceY < tapMovementThreshold
      ) {
        this.actualClick(e, 'touch');
        if (this.props.onItemTap) {
          this.props.onItemTap(this.itemId, e);
        }
      }
    }
  };

  handleDoubleClick = (e: React.MouseEvent) => {
    const target = e.target as Element;

    // to exclude context menu button from double click
    const isButton = target.className.includes('Button__ButtonText');

    // if clicked on context menu button, ignore double click
    if (isButton) {
      e.stopPropagation();
      e.preventDefault();
      return;
    }
    e.stopPropagation();
    if (this.props.onItemDoubleClick) {
      this.props.onItemDoubleClick(this.itemId, e);
    }
  };

  handleContextMenu = (e: React.MouseEvent) => {
    if (this.props.onContextMenu) {
      e.preventDefault();
      e.stopPropagation();
      this.props.onContextMenu(this.itemId, e);
    }
  };

  actualClick(
    e: React.MouseEvent | React.TouchEvent<HTMLElement>,
    clickType: 'click' | 'touch',
  ) {
    if (this.props.canSelect && this.props.onSelect) {
      this.props.onSelect(this.itemId, clickType, e);
    }
  }

  getItemRef = (el: HTMLElement) => (this.item = el);
  getDragLeftRef = (el: HTMLElement | null) => (this.dragLeft = el);
  getDragRightRef = (el: HTMLElement | null) => (this.dragRight = el);

  getItemProps = (props: GetItemPropsParams = {}): RealItemProps => {
    // TODO: maybe shouldn't include all of these classes
    let className = 'rct-item';
    const itemClassName = this.props.item.className;
    const propsClassName = props.className;
    if (itemClassName) {
      className += ` ${itemClassName}`;
    }
    if (propsClassName) {
      className += ` ${propsClassName}`;
    }

    return {
      className,
      key: this.itemId,
      ref: this.getItemRef,
      title: this.itemDivTitle,
      style: this.getItemStyle(props),
      onMouseUp: composeEvents(true, this.onMouseUp, props.onMouseUp),
      onTouchEnd: composeEvents(false, this.onTouchEnd, props.onTouchEnd),
      onMouseDown: composeEvents(true, this.onMouseDown, props.onMouseDown),
      onTouchStart: composeEvents(true, this.onTouchStart, props.onTouchStart),
      onDoubleClick: composeEvents(
        true,
        this.handleDoubleClick,
        props.onDoubleClick,
      ),
      onContextMenu: composeEvents(
        true,
        this.handleContextMenu,
        props.onContextMenu,
      ),
    };
  };

  getResizeProps = (
    props: {
      leftClassName?: string;
      rightClassName?: string;
      leftStyle?: CSSProperties;
      rightStyle?: CSSProperties;
    } = {},
  ) => {
    let leftName =
      'rct-item-handler rct-item-handler-left rct-item-handler-resize-left';
    if (props.leftClassName) {
      leftName += ` ${props.leftClassName}`;
    }

    let rightName =
      'rct-item-handler rct-item-handler-right rct-item-handler-resize-right';
    if (props.rightClassName) {
      rightName += ` ${props.rightClassName}`;
    }
    return {
      left: {
        ref: this.getDragLeftRef,
        className: leftName,
        style: Object.assign({}, leftResizeStyle, props.leftStyle),
      },
      right: {
        ref: this.getDragRightRef,
        className: rightName,
        style: Object.assign({}, rightResizeStyle, props.rightStyle),
      },
    };
  };

  getItemStyle(props: { style?: CSSProperties }) {
    const { selected, dimensions } = this.props;
    const { dragging } = this.state;

    const baseStyles = {
      position: 'absolute',
      boxSizing: 'border-box',
      left: `${dimensions.left}px`,
      top: `${dimensions.top}px`,
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`,
      lineHeight: `${dimensions.height}px`,
    };

    const canResizeLeft = selected && this.canResizeLeft(this.props);
    const canResizeRight = selected && this.canResizeRight(this.props);
    return Object.assign(
      {},
      overridableStyles,
      selected ? selectedStyle : {},
      selected && this.canMove(this.props) ? selectedAndCanMove : {},
      canResizeLeft ? selectedAndCanResizeLeft : {},
      canResizeLeft && dragging ? selectedAndCanResizeLeftAndDragLeft : {},
      canResizeRight ? selectedAndCanResizeRight : {},
      canResizeRight && dragging ? selectedAndCanResizeRightAndDragRight : {},
      props.style,
      baseStyles,
    );
  }

  render() {
    if (typeof this.props.order === 'undefined' || this.props.order === null) {
      return null;
    }

    const timelineContext = this.props.getTimelineContext();
    const itemContext: ItemContext = {
      dimensions: this.props.dimensions,
      useResizeHandle: this.props.useResizeHandle,
      title: this.itemTitle,
      canMove: this.canMove(this.props),
      canResizeLeft: this.canResizeLeft(this.props),
      canResizeRight: this.canResizeRight(this.props),
      selected: this.props.selected,
      dragging: this.state.dragging,
      dragStart: this.state.dragStart,
      dragTime: this.state.dragTime,
      dragGroupDelta: this.state.dragGroupDelta,
      resizing: this.state.resizing,
      resizeEdge: this.state.resizeEdge,
      resizeStart: this.state.resizeStart,
      resizeTime: this.state.resizeTime,
      width: this.props.dimensions.width,
      timeRatio: this.props.timeRatio,
      pixelsPerMs: this.props.pixelsPerMs,
    };

    return this.props.itemRenderer({
      item: this.props.item,
      timelineContext,
      itemContext,
      getItemProps: this.getItemProps,
      getResizeProps: this.getResizeProps,
    });
  }
}
